import { useTranslation } from "react-i18next";

import { FilterControl } from "./FilterControl";
import { ToolbarActions } from "./ToolbarActions";
import type { TableToolbarComponentProps } from "../../types";

import SearchInput from "@/components/ui/v2/search-input";

/**
 * TableToolbarComponent - renders a table toolbar with search, filters, and actions.
 * Layout: Search (left) | Actions + Filters (right)
 */
function TableToolbarComponent({
  search: searchConfig,
  filters = [],
  actions = [],
  searchValue,
  onSearchChange,
  filterValues = {},
  onFilterChange,
  customToolbarActions,
  dateRangePickerOptions,
  executeAction,
  renderChild,
}: TableToolbarComponentProps) {
  const { t } = useTranslation();

  const showSearch = searchConfig?.enabled !== false && onSearchChange != null;
  const toolbarFilters = filters.filter((f) => f.type !== "Search");
  const searchRowFilters = toolbarFilters.filter((f) => f.inlineWithSearch === true);
  const actionRowFilters = toolbarFilters.filter((f) => f.inlineWithSearch !== true);
  const hasSearchRowFilters = searchRowFilters.length > 0 && onFilterChange;
  const hasActionRowFilters = actionRowFilters.length > 0 && onFilterChange;

  const renderFilter = (filter: (typeof filters)[number]) => (
    <FilterControl
      key={filter.key}
      filter={filter}
      value={filterValues[filter.key] ?? filter.defaultValue}
      onChange={(value) => onFilterChange!(filter.key, value)}
      t={t}
      dateRangePickerOptions={dateRangePickerOptions}
    />
  );

  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      {/* Left: Search + filters that have inlineWithSearch */}
      <div className="flex flex-1 flex-wrap items-center gap-2 min-w-0">
        {showSearch && onSearchChange && (
          <div className="min-w-[200px] max-w-md flex-1">
            <SearchInput
              placeholder={
                searchConfig?.placeholder
                  ? t(searchConfig.placeholder)
                  : t("common.search")
              }
              onChange={(e) => onSearchChange(e.target.value)}
              value={searchValue ?? ""}
            />
          </div>
        )}
        {hasSearchRowFilters && searchRowFilters.map(renderFilter)}
      </div>

      {/* Right: Actions + remaining filters */}
      <div className="flex flex-wrap items-center gap-2">
        <ToolbarActions
          actions={actions}
          customToolbarActions={customToolbarActions}
          executeAction={executeAction}
          renderChild={renderChild}
        />
        {hasActionRowFilters && actionRowFilters.map(renderFilter)}
      </div>
    </div>
  );
}

export { TableToolbarComponent };
